import { useCallback, useEffect, useRef, useState } from 'react';
import { useChannelStateContext, useChatContext } from 'stream-chat-react';

/** Delay before an in-progress message is persisted as a draft while typing. */
const DRAFT_SAVE_DEBOUNCE_MS = 400;

/** Joins a parked draft to in-progress text, both when persisting and when resuming. */
const DRAFT_MERGE_SEPARATOR = '\n';

/**
 * Value returned by {@link useDraftReply}, describing the current saved draft
 * and the actions for resuming or discarding it.
 */
export type DraftReply = {
  /** Text of the saved, unsent draft to preview, or `null` when there is nothing to show. */
  draftText: string | null;
  /** Prepends the draft to the message input so the user can keep editing it. */
  resumeDraft: () => void;
  /** Permanently deletes the draft and hides the preview bar. */
  discardDraft: () => void;
};

/**
 * Manages an unsent message draft for the active channel.
 *
 * Stream persists drafts server-side and auto-loads them into the composer when
 * a channel is (re)watched. Instead of leaving that text in the input — which is
 * what made previously sent messages reappear (IP-1771) — this hook moves the
 * draft into a dismissible preview bar and keeps the input empty until the user
 * opts in.
 *
 * Two rules keep that from eating the user's own words (IP-2191):
 *
 * - A draft is only ever parked out of a composer that holds nothing but that
 *   draft. Stream re-sets `draftId` on every channel re-query, not just on
 *   channel entry, so a truthy `draftId` is no proof the user just arrived.
 * - A parked draft is never destroyed on its own. It shares the channel's single
 *   server-side draft slot with whatever is being typed, so both are persisted
 *   together and only resuming or discarding removes the parked half.
 *
 * @returns The draft preview text plus the resume and discard actions.
 */
export const useDraftReply = (): DraftReply => {
  const { channel } = useChannelStateContext('useDraftReply');
  const { client } = useChatContext('useDraftReply');
  const composer = channel.messageComposer;

  const [draftText, setDraftText] = useState<string | null>(null);

  // Refs mirror the latest values so subscriptions and the unmount cleanup read
  // fresh state without re-running the effect on every change.
  const draftTextRef = useRef<string | null>(null);
  const hasServerDraftRef = useRef(false);
  const isParkingRef = useRef(false);
  /** True only while a re-hydration we allowed is in flight. See the effect. */
  const isAdoptingDraftRef = useRef(false);
  /** Whether the user has typed since this hook adopted the channel. */
  const hasUserTypedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setDraft = useCallback((text: string | null) => {
    draftTextRef.current = text;
    setDraftText(text);
  }, []);

  const saveDraft = useCallback(
    (text: string) => {
      hasServerDraftRef.current = true;
      channel.createDraft({ id: composer.state.getLatestValue().id, text }).catch(() => undefined);
    },
    [channel, composer]
  );

  const deleteServerDraft = useCallback(() => {
    if (!hasServerDraftRef.current) return;
    hasServerDraftRef.current = false;
    channel.deleteDraft().catch(() => undefined);
  }, [channel]);

  /**
   * Persists what the preview bar and the input together would restore, as one
   * draft. Stream stores a single draft per channel, so a parked draft and
   * in-progress text have to share it — saving the merge is what stops composing
   * a new message from destroying the parked one.
   */
  const persistDraft = useCallback(() => {
    const merged = [draftTextRef.current, composer.textComposer.text.trim()]
      .filter((part): part is string => Boolean(part))
      .join(DRAFT_MERGE_SEPARATOR);

    if (!merged) {
      deleteServerDraft();
      return;
    }
    saveDraft(merged);
  }, [composer, deleteServerDraft, saveDraft]);

  /**
   * Moves a draft that Stream auto-loaded into the composer into the preview
   * bar, clearing the input so it stays empty until the user resumes the draft.
   */
  const parkLoadedDraft = useCallback(() => {
    const { draftId } = composer.state.getLatestValue();
    if (!draftId) return;

    const text = composer.textComposer.text.trim();
    // Null draftId before clearing the text: Stream's own state subscription
    // deletes the server draft as soon as the composition looks empty.
    composer.state.partialNext({ draftId: null });
    // Guard the programmatic clear so the text subscription doesn't treat it as
    // the user emptying the input.
    isParkingRef.current = true;
    composer.textComposer.setText('');
    isParkingRef.current = false;

    if (!text) return;
    hasServerDraftRef.current = true;
    setDraft(text);
  }, [composer, setDraft]);

  /**
   * Acknowledges a draft Stream re-loaded into a composer the user is working
   * in, without touching their text.
   *
   * Defensive: with the wrapper in the effect below, every re-hydration we let
   * through is flagged, so this should not be reachable. It clears `draftId`
   * rather than simply returning because a stale non-null `draftId` sends the
   * next channel response that carries no draft down
   * `initStateFromChannelResponse`'s `else if (draftId) this.clear()` branch,
   * which would empty the composer outright.
   */
  const releaseLoadedDraft = useCallback(() => {
    if (!composer.state.getLatestValue().draftId) return;
    composer.state.partialNext({ draftId: null });
  }, [composer]);

  const resumeDraft = useCallback(() => {
    const parked = draftTextRef.current;
    if (!parked) return;

    // The parked text was written first, so it leads and anything already typed
    // follows it — the "previous text" + "this now" case joins rather than drops.
    const typed = composer.textComposer.text;
    const merged = typed.trim() ? `${parked}${DRAFT_MERGE_SEPARATOR}${typed}` : parked;

    composer.textComposer.setText(merged);
    composer.textComposer.setSelection({ start: merged.length, end: merged.length });
    setDraft(null);
    // Focus the composer after the text is committed so the user can keep typing.
    requestAnimationFrame(() => {
      const textarea = document.querySelector<HTMLTextAreaElement>('[data-testid="message-input"]');
      textarea?.focus();
    });
  }, [composer, setDraft]);

  const discardDraft = useCallback(() => {
    setDraft(null);
    persistDraft();
  }, [persistDraft, setDraft]);

  useEffect(() => {
    // A channel switch reuses this hook instance (<Channel> doesn't remount its
    // children), so clear any state/refs/pending timer left over from the
    // previous channel before adopting the new one. Otherwise one conversation's
    // parked draft could bleed into another, and a pending save/delete could
    // target the wrong channel.
    clearTimeout(saveTimerRef.current);
    hasServerDraftRef.current = false;
    isParkingRef.current = false;
    isAdoptingDraftRef.current = false;
    hasUserTypedRef.current = false;
    setDraft(null);

    // Stream re-hydrates the composer from EVERY channel API response: both
    // Channel.query() and client.queryChannels(), and the latter runs on every
    // websocket reconnect via recoverState(). initStateFromChannelResponse()
    // replaces the composer text with the last saved draft snapshot and only
    // then sets draftId, so backgrounding a tab for 35s used to roll an
    // in-progress message back to the last debounced save (IP-2191). A composer
    // the user is typing in is never re-hydrated.
    const rehydrateFromChannelResponse = composer.initStateFromChannelResponse;
    composer.initStateFromChannelResponse = (response) => {
      // Gated on the user having typed here, not merely on the composer holding
      // text: the composer belongs to the cached Channel and keeps its text
      // across unmount, so on re-entry that text is leftover rather than live.
      // Refusing to re-hydrate it would strand the parked half of the saved
      // draft, which nothing else can recover.
      if (hasUserTypedRef.current && composer.textComposer.text.trim()) return;

      // Whatever this loads is therefore a saved draft, not something being
      // written. initState is synchronous, so the draftId subscription below
      // sees this flag.
      isAdoptingDraftRef.current = true;
      try {
        rehydrateFromChannelResponse(response);
      } finally {
        isAdoptingDraftRef.current = false;
      }
    };

    // The route watches the channel before this mounts, so a pre-existing draft
    // is already loaded into the composer.
    parkLoadedDraft();

    const unsubscribeDraftId = composer.state.subscribeWithSelector(
      (state) => ({ draftId: state.draftId }),
      ({ draftId }) => {
        if (!draftId) return;

        // Channel entry is handled above, so anything arriving here came from a
        // re-hydration. Park it only when it landed in an empty composer;
        // otherwise the text in there is the user's own and moving it out of
        // the input is exactly the defect this guards against.
        if (!isAdoptingDraftRef.current) {
          releaseLoadedDraft();
          return;
        }
        parkLoadedDraft();
      }
    );

    const unsubscribeText = composer.textComposer.state.subscribeWithSelector(
      (state) => ({ text: state.text }),
      ({ text }, previous) => {
        if (previous === undefined) return; // initial immediate fire
        if (isParkingRef.current) return;

        hasUserTypedRef.current = true;
        clearTimeout(saveTimerRef.current);

        if (text.trim()) {
          // The user is composing: (re)save shortly after they stop typing.
          saveTimerRef.current = setTimeout(persistDraft, DRAFT_SAVE_DEBOUNCE_MS);
          return;
        }

        // Input emptied by the user or by sending. A parked draft survives it:
        // only resuming or discarding removes that.
        persistDraft();
      }
    );

    const { unsubscribe: unsubscribeMessage } = channel.on('message.new', (event) => {
      if (event.user?.id !== client.userID) return;
      // What was sent is no longer a draft, but a parked draft the user never
      // resumed is not part of that message and must outlive it.
      clearTimeout(saveTimerRef.current);
      persistDraft();
    });

    return () => {
      clearTimeout(saveTimerRef.current);
      unsubscribeDraftId();
      unsubscribeText();
      unsubscribeMessage();
      composer.initStateFromChannelResponse = rehydrateFromChannelResponse;

      // Flush anything the debounce didn't get to persist yet.
      persistDraft();
    };
  }, [
    channel,
    client.userID,
    composer,
    parkLoadedDraft,
    persistDraft,
    releaseLoadedDraft,
    setDraft,
  ]);

  return {
    draftText,
    resumeDraft,
    discardDraft,
  };
};
